import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Breadcrumbs } from "../../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../../components/useBreadcrumbBack";
import { useAuth } from "../../../../providers/AuthProvider";
import { useFormFeedback } from "../../../../hooks/useFormFeedback";
import { ApiError } from "../../../../services/apiClient";
import {
  getGoogleAuthUrl,
  getFacebookAuthUrl,
  listOAuthConnections,
  resendVerificationEmail,
  unlinkOAuthProvider
} from "../../../auth/api/auth";
import type { OAuthConnection, ThemePreference, UpdateProfilePayload } from "../../../../types/auth";
import { storage } from "../../../shared/api/storage";
import {
  AUTH_LINK_PROVIDER_KEY,
  AUTH_REDIRECT_KEY,
  PROFILE_NOTICE_KEY,
} from "../../../../providers/AuthProvider";

import { PageShell } from "../../../shared/layouts/PageShell";
import { PageHeader } from "../../../shared/ui/layouts/PageHeader";
import { SkeletonCard } from "../../../shared/ui/skeletons";

import {
  ArrowLeft,
  FloppyDisk,
  LinkSimple,
  LinkSimpleBreak,
  PaperPlaneTilt,
  DeviceMobile,
  ShieldCheck,
  ClockClockwise,
  Moon,
  SunDim
} from "@phosphor-icons/react";
import { PasswordField } from "../../../../components/PasswordField";
import { LanguageSelect } from "../../../../components/LanguageSelect";
import { defaultIconProps } from "../../../shared/ui/icons";
import { Avatar } from "../../../shared/ui/Avatar";
import { PhoneField } from "../../../../components/PhoneField";
import { resolvePhoneCountry } from "../../../../phone/countryData";
import { FormFieldLabel } from "../../../../components/FormFieldLabel";
import { GeoLocationSelect, type GeoSelection } from "../../../../components/geo/GeoLocationSelect";
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  normalizeLocale
} from "../../../../i18n";
import { ConnectedAccountProvider } from "../../../../components/ConnectedAccountProvider";
import { ConfirmDialog } from "../../../../components/ConfirmDialog";
import { createLogger } from "../../../../utils/logger";
import { useScrollToAnchor } from "../../../../hooks/useScrollToAnchor";

const logger = createLogger("ProfileSettingsPage");

const DEFAULT_MAX_PROFILE_PHOTO_BYTES = 5 * 1024 * 1024;
const parsedMaxProfileBytes = Number(
  import.meta.env.VITE_PROFILE_PHOTO_MAX_UPLOAD_BYTES ?? DEFAULT_MAX_PROFILE_PHOTO_BYTES
);
const MAX_PROFILE_PHOTO_BYTES = Number.isFinite(parsedMaxProfileBytes) && parsedMaxProfileBytes > 0
  ? parsedMaxProfileBytes
  : DEFAULT_MAX_PROFILE_PHOTO_BYTES;
const ALLOWED_PROFILE_PHOTO_TYPES = ["image/png", "image/jpeg", "image/webp"];

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const headerActionsClasses = "flex flex-wrap items-center gap-3";
const sectionsStackClasses = "grid grid-cols-1 gap-6 xl:grid-cols-2 items-start";

const cardClasses = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900";
const cardHeaderClasses = "mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between";
const cardTitleClasses = "text-lg font-semibold text-slate-900 dark:text-slate-100";
const cardDescriptionClasses = "text-sm text-slate-500 dark:text-slate-400";

const formStackClasses = "flex flex-col gap-6";
const formFieldClasses = "flex flex-col gap-2";
const formLabelClasses = "block text-sm font-medium text-slate-700 dark:text-slate-300";
const inputClasses = "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const formActionsClasses = "flex flex-wrap items-center justify-end gap-3 pt-2";

const primaryButtonClasses = "pm-btn pm-btn-primary";
const secondaryButtonClasses = "pm-btn pm-btn-surface";
const ghostButtonClasses = "pm-btn pm-btn-ghost";

const badgeBaseClasses = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium";
const badgeVariantClasses = {
  verified: "bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-900/50",
  pending: "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-900/50",
  unverified: "bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};

const noticeBaseClasses = "flex items-start gap-3 rounded-xl p-4 text-sm";
const noticeSuccessClasses = "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300";
const noticeWarningClasses = "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300";
const pendingMessageClasses = "rounded-xl bg-amber-50 p-4 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300";

const connectionCardClasses =
  "flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 dark:bg-slate-900";

const themeOptionsGridClasses = "grid grid-cols-2 gap-3";
const themeOptionClasses = "flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:focus:ring-offset-slate-900";
const themeOptionActiveClasses = "border-sky-600 bg-sky-50 text-sky-700 ring-1 ring-sky-600 dark:border-sky-500 dark:bg-sky-900/30 dark:text-sky-400 dark:ring-sky-500";

const photoSectionClasses =
  "grid gap-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-6 sm:grid-cols-[auto,1fr] lg:grid-cols-[auto,1fr,auto] lg:items-center dark:border-slate-700 dark:bg-slate-800/50";
const photoDetailsClasses =
  "flex flex-col gap-1 text-sm text-slate-500 text-center sm:text-left dark:text-slate-400";
const photoActionsClasses =
  "flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end";

const APP_ENVIRONMENT =
  (import.meta.env.VITE_ENVIRONMENT ?? import.meta.env.MODE ?? "").toLowerCase();
const ALLOW_DEV_BYPASS = APP_ENVIRONMENT !== "production";

const deriveDisplayName = (value: string) =>
  value.trim().split(/\s+/).filter(Boolean)[0] ?? "";

export function ProfileSettingsPage() {
  useScrollToAnchor();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    user,
    accessToken,
    updateProfile,
    refreshUser,
    uploadProfilePhoto,
    requestPhoneVerification,
    verifyPhoneCode,
    bypassPhoneVerification,
    bypassEmailVerification,
    logout
  } = useAuth();
  const { setError, setSuccess, resetFeedback } = useFormFeedback();
  const [fullName, setFullName] = useState("");
  const [name, setName] = useState("");
  const [hasEditedDisplayName, setHasEditedDisplayName] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isNameSubmitting, setIsNameSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [isResendingVerification, setIsResendingVerification] =
    useState(false);
  const [isPhotoPreferenceSubmitting, setIsPhotoPreferenceSubmitting] =
    useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [connections, setConnections] = useState<OAuthConnection[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
  const [isUnlinkingGoogle, setIsUnlinkingGoogle] = useState(false);
  const [isLinkingFacebook, setIsLinkingFacebook] = useState(false);
  const [isUnlinkingFacebook, setIsUnlinkingFacebook] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    "disconnect-google" | "disconnect-facebook" | "signout-others" | null
  >(null);
  const [phoneValue, setPhoneValue] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [isRequestingPhone, setIsRequestingPhone] = useState(false);
  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false);
  const [isDevVerifyingPhone, setIsDevVerifyingPhone] = useState(false);
  const [isDevVerifyingEmail, setIsDevVerifyingEmail] = useState(false);
  const [didRequestPhone, setDidRequestPhone] = useState(false);
  const [preferredLocale, setPreferredLocale] = useState(DEFAULT_LOCALE);
  const [themePreference, setThemePreference] = useState<ThemePreference>("dark");
  const [isPreferencesSubmitting, setIsPreferencesSubmitting] = useState(false);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [addressSiteDescription, setAddressSiteDescription] = useState("");
  const [addressPostalCode, setAddressPostalCode] = useState("");
  const [addressGeo, setAddressGeo] = useState<GeoSelection>({});
  const [isAddressSubmitting, setIsAddressSubmitting] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const refreshUserRef = useRef(false);

  const handlePhotoUploadClick = () => {
    if (isUploadingPhoto) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handlePhotoFileChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!file) {
      return;
    }

    if (file.size > MAX_PROFILE_PHOTO_BYTES) {
      const maxSizeMB = MAX_PROFILE_PHOTO_BYTES / (1024 * 1024);
      const formattedMax = Number.isInteger(maxSizeMB)
        ? `${maxSizeMB.toFixed(0)}MB`
        : `${maxSizeMB.toFixed(1)}MB`;
      setError(t("settings.profile.photo.fileTooLarge", { size: formattedMax }));
      return;
    }

    if (file.type && !ALLOWED_PROFILE_PHOTO_TYPES.includes(file.type)) {
      setError(t("settings.profile.photo.unsupportedFormat"));
      return;
    }

    resetFeedback();
    setIsUploadingPhoto(true);
    try {
      await uploadProfilePhoto(file);
      setSuccess(t("settings.profile.photo.uploadSuccess"));
    } catch (err) {
      logger.error("Failed to upload profile photo", err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t("settings.profile.photo.uploadError"));
      }
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleDevEmailBypass = async () => {
    if (!showDevEmailBypass) {
      return;
    }
    if (!user) {
      return;
    }
    const targetEmail = (user.pending_email ?? user.email ?? "").trim();
    if (!targetEmail) {
      setError(t("forms.email.required"));
      return;
    }
    resetFeedback();
    setIsDevVerifyingEmail(true);
    try {
      const updated = await bypassEmailVerification();
      setSuccess(t("settings.profile.email.devVerifySuccess"));
      if (updated.email) {
        setEmailInput(updated.email);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.email.devVerifyError");
      setError(message);
    } finally {
      setIsDevVerifyingEmail(false);
    }
  };

  useEffect(() => {
    if (user) {
      const initialFullName = user.full_name ?? user.name ?? "";
      setFullName(initialFullName);
      setName(user.name ?? "");
      setHasEditedDisplayName(false);
      setEmailInput(user.pending_email ?? user.email ?? "");
      const initialPhone = user.pending_phone ?? user.phone_number ?? "";
      setPhoneValue(initialPhone);
      setPhoneCode("");
      setDidRequestPhone(Boolean(user.pending_phone));
      setPreferredLocale(normalizeLocale(user.preferred_locale ?? DEFAULT_LOCALE));
      const appearanceTheme =
        user.preferences?.appearance?.theme === "light" ? "light" : "dark";
      setThemePreference(appearanceTheme);
      setAddressLine1(user.address?.line1 ?? "");
      setAddressLine2(user.address?.line2 ?? "");
      setAddressSiteDescription(user.address?.site_description ?? "");
      setAddressPostalCode(user.address?.postal_code ?? "");
      setAddressGeo({
        regionId: user.address?.region_id ?? undefined,
        provinceId: user.address?.province_id ?? undefined,
        municipalityId: user.address?.municipality_id ?? undefined,
        barangayId: user.address?.barangay_id ?? undefined
      });
    }
  }, [user]);

  useEffect(() => {
    if (!accessToken || refreshUserRef.current) {
      return;
    }
    refreshUserRef.current = true;
    void refreshUser();
  }, [accessToken, refreshUser]);

  useEffect(() => {
    const notice = storage.get<{ type: "success" | "error"; message: string }>(
      PROFILE_NOTICE_KEY,
    );
    if (notice) {
      if (notice.type === "success") {
        setSuccess(notice.message);
      } else {
        setError(notice.message);
      }
      storage.remove(PROFILE_NOTICE_KEY);
    }
  }, [setError, setSuccess]);

  const loadConnections = useCallback(async () => {
    if (!accessToken) {
      setConnections([]);
      setIsLoadingConnections(false);
      return;
    }
    setIsLoadingConnections(true);
    try {
      const result = await listOAuthConnections(accessToken);
      setConnections(result);
    } catch (err) {
      logger.error("Failed to load OAuth connections", err);
      setError((prev) => prev ?? t("auth.oauth.connected_accounts.loadError"));
      setConnections([]);
    } finally {
      setIsLoadingConnections(false);
    }
  }, [accessToken, setError, t]);

  useEffect(() => {
    if (accessToken) {
      void loadConnections();
    }
  }, [accessToken, loadConnections]);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [{ label: t("settings.profile.title") }];
  }, [t]);

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/dashboard" },
  });

  const profilePhoto = user?.profile_photo ?? null;
  const photoSourceLabel = useMemo(() => {
    const source = profilePhoto?.source?.toLowerCase();
    if (!source) {
      return null;
    }
    const sourceLabels: Record<string, string> = {
      google: t("auth.oauth.google"),
      facebook: t("auth.oauth.facebook"),
      gravatar: t("settings.profile.photo.source.gravatar"),
      upload: t("settings.profile.photo.source.upload"),
      generated: t("settings.profile.photo.source.generated")
    };
    if (sourceLabels[source]) {
      return sourceLabels[source];
    }
    return source.charAt(0).toUpperCase() + source.slice(1);
  }, [profilePhoto?.source, t]);

  const localeOptions = useMemo(
    () =>
      SUPPORTED_LOCALES.map((locale) => ({
        value: locale.code,
        label: t(locale.labelKey, { defaultValue: locale.nativeLabel })
      })),
    [t]
  );

  const isGoogleConnected = connections.some(
    (connection) => connection.provider === "google",
  );
  const googleConnection = connections.find(
    (connection) => connection.provider === "google",
  );

  const isFacebookConnected = connections.some(
    (connection) => connection.provider === "facebook",
  );
  const facebookConnection = connections.find(
    (connection) => connection.provider === "facebook",
  );

  const handleConnectGoogle = async () => {
    if (!accessToken || !user) {
      return;
    }
    resetFeedback();
    setIsLinkingGoogle(true);
    try {
      storage.set(AUTH_REDIRECT_KEY, "/settings/profile");
      storage.set(AUTH_LINK_PROVIDER_KEY, "google");
      const { auth_url } = await getGoogleAuthUrl();
      window.location.href = auth_url;
    } catch (err) {
      logger.error("Failed to start Google linking", err);
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.oauth.googleLinkError");
      setError(message);
      storage.remove(AUTH_LINK_PROVIDER_KEY);
    } finally {
      setIsLinkingGoogle(false);
    }
  };

  const handleDisconnectGoogle = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    resetFeedback();
    setIsUnlinkingGoogle(true);
    try {
      await unlinkOAuthProvider(accessToken, "google");
      setSuccess(t("settings.profile.oauth.googleDisconnected"));
      await loadConnections();
      await refreshUser();
    } catch (err) {
      logger.error("Failed to disconnect Google", err);
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.oauth.googleDisconnectError");
      setError(message);
    } finally {
      setIsUnlinkingGoogle(false);
    }
  }, [accessToken, loadConnections, refreshUser, resetFeedback, setError, setSuccess, t]);

  const handleConnectFacebook = async () => {
    if (!accessToken) {
      return;
    }
    resetFeedback();
    setIsLinkingFacebook(true);
    try {
      storage.set(AUTH_REDIRECT_KEY, "/settings/profile");
      storage.set(AUTH_LINK_PROVIDER_KEY, "facebook");
      const { auth_url } = await getFacebookAuthUrl();
      window.location.href = auth_url;
    } catch (err) {
      logger.error("Failed to start Facebook linking", err);
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.oauth.facebookLinkError");
      setError(message);
      storage.remove(AUTH_LINK_PROVIDER_KEY);
    } finally {
      setIsLinkingFacebook(false);
    }
  };

  const handleDisconnectFacebook = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    resetFeedback();
    setIsUnlinkingFacebook(true);
    try {
      await unlinkOAuthProvider(accessToken, "facebook");
      setSuccess(t("settings.profile.oauth.facebookDisconnected"));
      await loadConnections();
      await refreshUser();
    } catch (err) {
      logger.error("Failed to disconnect Facebook", err);
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.oauth.facebookDisconnectError");
      setError(message);
    } finally {
      setIsUnlinkingFacebook(false);
    }
  }, [accessToken, loadConnections, refreshUser, resetFeedback, setError, setSuccess, t]);

  const confirmDialogConfig = useMemo(() => {
    switch (confirmAction) {
      case "disconnect-google":
        return {
          title: t("settings.profile.oauth.disconnectGoogleConfirm"),
          confirmLabel: t("auth.oauth.disconnect"),
          loading: isUnlinkingGoogle,
          tone: "danger" as const,
          action: handleDisconnectGoogle,
        };
      case "disconnect-facebook":
        return {
          title: t("settings.profile.oauth.disconnectFacebookConfirm"),
          confirmLabel: t("auth.oauth.disconnect"),
          loading: isUnlinkingFacebook,
          tone: "danger" as const,
          action: handleDisconnectFacebook,
        };
      case "signout-others":
        return {
          title: t(
            "settings.profile.password.signOutOthers",
            "Sign out of other devices now?"
          ),
          confirmLabel: t(
            "settings.profile.sessions.revoke_all",
            "Sign out everywhere"
          ),
          loading: false,
          tone: "danger" as const,
          action: () => {
            navigate("/settings/sessions");
          },
        };
      default:
        return null;
    }
  }, [confirmAction, handleDisconnectFacebook, handleDisconnectGoogle, isUnlinkingFacebook, isUnlinkingGoogle, navigate, t]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmDialogConfig) {
      return;
    }
    try {
      await confirmDialogConfig.action();
    } finally {
      setConfirmAction(null);
    }
  }, [confirmDialogConfig]);

  if (!user || !accessToken) {
    return null;
  }

  const handleAddressSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetFeedback();

    // Preserve backend-managed coordinates so background enrichment can keep them in sync.
    const payloadAddress = {
      line1: addressLine1.trim() || undefined,
      line2: addressLine2.trim() || undefined,
      site_description: addressSiteDescription.trim() || undefined,
      postal_code: addressPostalCode.trim() || undefined,
      region_id: addressGeo.regionId,
      province_id: addressGeo.provinceId,
      municipality_id: addressGeo.municipalityId,
      barangay_id: addressGeo.barangayId,
      latitude: typeof user.address?.latitude === "number" ? user.address.latitude : undefined,
      longitude: typeof user.address?.longitude === "number" ? user.address.longitude : undefined
    };

    setIsAddressSubmitting(true);
    try {
      const result = await updateProfile({ address: payloadAddress });
      if (result.kind === "updated") {
        setSuccess(t("settings.profile.address.updateSuccess"));
      } else {
        setSuccess(result.message);
      }
    } catch (err) {
      logger.error("Failed to update address", err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t("settings.profile.address.updateError"));
      }
    } finally {
      setIsAddressSubmitting(false);
    }
  };

  const headingTitle = user.name ?? user.email ?? t("settings.profile.title");
  const headingSubtitle = t("settings.profile.subtitle");

  const profilePhotoUrl = profilePhoto?.download_url ?? null;
  const profilePhotoFallbackUrl = profilePhoto?.remote_url ?? null;
  const isProfilePhotoHidden = profilePhoto?.opt_out ?? false;
  const photoRemoteURL = profilePhoto?.remote_url ?? null;
  const photoCheckedAt = profilePhoto?.checked_at
    ? new Date(profilePhoto.checked_at).toLocaleString()
    : null;

  const originalEmail = user.pending_email ?? user.email ?? "";
  const emailStatus = user.pending_email
    ? t("settings.profile.email.status_pending")
    : user.email_verified
    ? t("settings.profile.email.status_verified")
    : t("settings.profile.email.status_unverified");
  const emailStatusVariant = user.pending_email
    ? "pending"
    : user.email_verified
    ? "verified"
    : "unverified";
  const isEmailUnchanged =
    emailInput.trim().toLowerCase() === originalEmail.toLowerCase();
  const canResendVerification = !user.email_verified;

  const phoneStatus = user.phone_verified
    ? t("settings.profile.phone.status_verified")
    : user.pending_phone
    ? t("settings.profile.phone.status_pending")
    : t("settings.profile.phone.status_unverified");
  const phoneStatusVariant = user.phone_verified
    ? "verified"
    : user.pending_phone
    ? "pending"
    : "unverified";

  const { localDigits: pendingPhoneDigits } = resolvePhoneCountry(phoneValue, "PH");
  const verifiedPhone = user.phone_number ?? "";
  const pendingPhone = user.pending_phone ?? "";
  const isPhoneValueSameAsVerified =
    verifiedPhone !== "" && phoneValue.trim() === verifiedPhone.trim();
  const isPhoneValueSameAsPending =
    pendingPhone !== "" && phoneValue.trim() === pendingPhone.trim();
  const hasPendingPhone = pendingPhone !== "";
  const disablePhoneRequest =
    isRequestingPhone ||
    pendingPhoneDigits.length === 0 ||
    (isPhoneValueSameAsVerified && !hasPendingPhone);
  const phoneRequestLabel = isPhoneValueSameAsPending
    ? t("settings.profile.phone.resendCode")
    : t("settings.profile.phone.sendCode");
  const showDevPhoneBypass = ALLOW_DEV_BYPASS && user?.phone_verified !== true;
  const showDevEmailBypass = ALLOW_DEV_BYPASS && user?.email_verified !== true;

  const handleFullNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFullName(value);
    if (!hasEditedDisplayName) {
      setName(deriveDisplayName(value));
    }
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setHasEditedDisplayName(true);
    setName(event.target.value);
  };

  const handleEmailInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmailInput(event.target.value);
  };

  const handleNameSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetFeedback();

    const trimmedFullName = fullName.trim();
    const trimmedName = (name.trim() || deriveDisplayName(trimmedFullName)).trim();
    if (!trimmedFullName) {
      setError(t("auth.register.name_required"));
      return;
    }
    if (trimmedFullName.length < 3) {
      setError(t("auth.register.full_name_min"));
      return;
    }
    if (!trimmedName) {
      setError(t("auth.register.name_required"));
      return;
    }
    if (trimmedName.length < 2) {
      setError(t("auth.register.display_name_min"));
      return;
    }
    const currentDisplayName = user.name ?? "";
    const currentFullName = user.full_name ?? user.name ?? "";
    if (trimmedName === currentDisplayName && trimmedFullName === currentFullName) {
      setError(t("settings.profile.noChanges"));
      return;
    }

    setIsNameSubmitting(true);
    try {
      const result = await updateProfile({ name: trimmedName, full_name: trimmedFullName });
      setSuccess(
        result.kind === "updated"
          ? t("settings.profile.updateSuccess")
          : result.message,
      );
      if (result.kind === "updated") {
        setName(result.user.name ?? trimmedName);
        setFullName(result.user.full_name ?? trimmedFullName);
        setHasEditedDisplayName(false);
      }
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : t("settings.profile.updateError");
      setError(message);
    } finally {
      setIsNameSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetFeedback();

    const trimmedCurrent = currentPassword.trim();
    const trimmedNew = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedCurrent || !trimmedNew || !trimmedConfirm) {
      setError(t("settings.profile.password.allFieldsRequired"));
      return;
    }

    if (trimmedNew.length < 8) {
      setError(t("forms.password.min_length"));
      return;
    }

    if (trimmedNew !== trimmedConfirm) {
      setError(t("settings.profile.password.mismatch"));
      return;
    }

    setIsPasswordSubmitting(true);
    try {
      const result = await updateProfile({
        current_password: trimmedCurrent,
        new_password: trimmedNew,
      });
      setSuccess(
        result.kind === "updated"
          ? t("settings.profile.password.updateSuccess")
          : result.message,
      );
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      if (accessToken) {
        setConfirmAction("signout-others");
      }
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : t("settings.profile.password.updateError");
      setError(message);
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetFeedback();

    const trimmedEmail = emailInput.trim();
    const currentEmail = user.pending_email ?? user.email ?? "";

    if (!trimmedEmail) {
      setError(t("forms.email.required"));
      return;
    }

    if (trimmedEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setError(t("settings.profile.email.noChanges"));
      return;
    }

    setIsEmailSubmitting(true);
    try {
      const result = await updateProfile({ email: trimmedEmail });
      if (result.kind === "email_verification") {
        setSuccess(result.message);
      } else {
        setSuccess(t("settings.profile.email.updateSuccess"));
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.email.updateError");
      setError(message);
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  const handleLocaleChange = (nextValue: string) => {
    setPreferredLocale(normalizeLocale(nextValue));
  };

  const handleThemeSelection = (value: ThemePreference) => {
    setThemePreference(value);
  };

  const handlePreferencesSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetFeedback();

    const existingLocale = normalizeLocale(user.preferred_locale ?? DEFAULT_LOCALE);
    const nextLocale = normalizeLocale(preferredLocale);
    const existingTheme = user.preferences?.appearance?.theme === "light" ? "light" : "dark";
    const nextTheme = themePreference === "light" ? "light" : "dark";

    const localeChanged = nextLocale !== existingLocale;
    const themeChanged = nextTheme !== existingTheme;

    if (!localeChanged && !themeChanged) {
      setError(t("settings.profile.preferences.noChanges"));
      return;
    }

    setIsPreferencesSubmitting(true);
    try {
      const payload: UpdateProfilePayload = {};
      if (localeChanged) {
        payload.preferred_locale = nextLocale;
      }
      if (themeChanged) {
        payload.preferences = {
          appearance: {
            theme: nextTheme
          }
        };
      }
      const result = await updateProfile(payload);
      setSuccess(
        result.kind === "updated"
          ? t("settings.profile.preferences.success")
          : result.message
      );
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.preferences.error");
      setError(message);
    } finally {
      setIsPreferencesSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!accessToken) {
      return;
    }
    resetFeedback();
    setIsResendingVerification(true);
    try {
      const targetEmail = (user.pending_email ?? user.email ?? "").trim();
      if (!targetEmail) {
        setError(t("forms.email.required"));
        setIsResendingVerification(false);
        return;
      }
      const response = await resendVerificationEmail(accessToken, targetEmail);
      setSuccess(response.message ?? t("settings.profile.email.verificationSent"));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.email.resendError");
      setError(message);
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handlePhoneValueChange = (next: string) => {
    setPhoneValue(next);
    setPhoneCode("");
    const normalizedNext = next.trim();
    if (!pendingPhone || normalizedNext !== pendingPhone.trim()) {
      setDidRequestPhone(false);
    } else {
      setDidRequestPhone(true);
    }
  };

  const handlePhoneRequest = async () => {
    if (!accessToken) {
      return;
    }
    if (!pendingPhoneDigits) {
      setError(t("settings.profile.phone.enterNumberFirst"));
      return;
    }

    resetFeedback();
    setIsRequestingPhone(true);
    try {
      await requestPhoneVerification(phoneValue);
      setDidRequestPhone(true);
      setSuccess(t("settings.profile.phone.codeSent"));
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.phone.sendCodeError");
      setError(message);
    } finally {
      setIsRequestingPhone(false);
    }
  };

  const handlePhoneVerify = async () => {
    if (!phoneCode.trim()) {
      setError(t("settings.profile.phone.enterCodeToVerify"));
      return;
    }
    resetFeedback();
    setIsVerifyingPhone(true);
    try {
      const updatedUser = await verifyPhoneCode(phoneCode.trim());
      setSuccess(t("settings.profile.phone.verifySuccess"));
      setPhoneCode("");
      setDidRequestPhone(false);
      if (updatedUser.email_verified && updatedUser.phone_verified) {
        navigate("/welcome/credits", { replace: true });
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.phone.verifyError");
      setError(message);
    } finally {
      setIsVerifyingPhone(false);
    }
  };

  const handleDevPhoneBypass = async () => {
    if (!showDevPhoneBypass) {
      return;
    }
    if (!pendingPhoneDigits && phoneValue.trim().length === 0 && !user.phone_number) {
      setError(t("settings.profile.phone.enterNumberFirst"));
      return;
    }

    resetFeedback();
    setIsDevVerifyingPhone(true);
    try {
      const updatedUser = await bypassPhoneVerification();
      setSuccess(t("settings.profile.phone.devVerifySuccess"));
      setPhoneCode("");
      setDidRequestPhone(false);
      if (updatedUser.phone_number) {
        setPhoneValue(updatedUser.phone_number);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.phone.devVerifyError");
      setError(message);
    } finally {
      setIsDevVerifyingPhone(false);
    }
  };

  const handleProfilePhotoToggle = async () => {
    if (!accessToken || !user) {
      return;
    }
    resetFeedback();
    setIsPhotoPreferenceSubmitting(true);
    try {
      const nextOptOut = !(user.profile_photo?.opt_out ?? false);
      const result = await updateProfile({
        profile_photo_opt_out: nextOptOut
      });
      if (result.kind === "updated") {
        setSuccess(
          nextOptOut
            ? t("settings.profile.photo.hiddenSuccess")
            : t("settings.profile.photo.preferenceUpdated")
        );
      } else {
        setSuccess(result.message);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("settings.profile.photo.preferenceError");
      setError(message);
    } finally {
      setIsPhotoPreferenceSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      logger.warn("Logout failed", err);
      setError(t("auth.signout.error"));
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={headingTitle}
        subtitle={headingSubtitle}
        actions={
          <div className={headerActionsClasses}>
            <button
              type="button"
              className="pm-btn pm-btn-ghost"
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              {isSigningOut ? t("auth.signout.signing_out") : t("auth.signout.button")}
            </button>
            <Link className={secondaryButtonClasses} to="/settings/sessions">
              <ShieldCheck {...defaultIconProps} aria-hidden />
              {t("settings.profile.sessions.manage", "Manage sessions")}
            </Link>
            <Link className={secondaryButtonClasses} to="/settings/activity">
              <ClockClockwise {...defaultIconProps} aria-hidden />
              {t("settings.profile.viewActivity")}
            </Link>
            <button type="button" className={ghostButtonClasses} onClick={handleBack}>
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />

      <div className={sectionsStackClasses}>
        <section
          id="profile-info"
          tabIndex={-1}
          className={cardClasses}
        >
          <div className={cardHeaderClasses}>
            <div>
              <h2 className={cardTitleClasses}>{t("settings.profile.information.title")}</h2>
              <p className={cardDescriptionClasses}>{t("settings.profile.information.description")}</p>
            </div>
          </div>

          <form className={formStackClasses} onSubmit={handleNameSubmit}>
            <div className={formFieldClasses}>
              <label className={formLabelClasses} htmlFor="profile-full-name">
                <FormFieldLabel label={t("forms.name.full_label")} />
              </label>
              <input
                id="profile-full-name"
                type="text"
                className={inputClasses}
                value={fullName}
                onChange={handleFullNameChange}
                placeholder={t("forms.name.full_name_placeholder")}
                autoComplete="name"
                disabled={isNameSubmitting}
              />
            </div>
            <div className={formFieldClasses}>
              <label className={formLabelClasses} htmlFor="profile-display-name">
                <FormFieldLabel label={t("forms.name.display_label")} />
              </label>
              <input
                id="profile-display-name"
                type="text"
                className={inputClasses}
                value={name}
                onChange={handleNameChange}
                placeholder={t("forms.name.display_placeholder")}
                autoComplete="nickname"
                disabled={isNameSubmitting}
              />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t("settings.profile.display_name_hint")}
              </p>
            </div>

            <div className={formActionsClasses}>
              <button
                type="submit"
                className={primaryButtonClasses}
                disabled={isNameSubmitting}
              >
                {isNameSubmitting ? (
                  t("actions.saving")
                ) : (
                  <>
                    <FloppyDisk {...defaultIconProps} aria-hidden />
                    {t("actions.save_name")}
                  </>
                )}
              </button>
            </div>
          </form>
        </section>

        <section
          id="profile-address"
          tabIndex={-1}
          className={cardClasses}
        >
          <div className={cardHeaderClasses}>
            <div>
              <h2 className={cardTitleClasses}>{t("settings.profile.address.title")}</h2>
              <p className={cardDescriptionClasses}>{t("settings.profile.address.description")}</p>
            </div>
          </div>

          <form className={formStackClasses} onSubmit={handleAddressSubmit}>
            <GeoLocationSelect
              includeBarangay
              value={addressGeo}
              onChange={setAddressGeo}
              disabled={isAddressSubmitting}
            />

            <div className={formFieldClasses}>
              <FormFieldLabel label={t("settings.profile.address.line1_label")} optional />
              <input
                type="text"
                className={inputClasses}
                value={addressLine1}
                onChange={(event) => setAddressLine1(event.target.value)}
                disabled={isAddressSubmitting}
              />
            </div>

            <div className={formFieldClasses}>
              <FormFieldLabel label={t("settings.profile.address.line2_label")} optional />
              <input
                type="text"
                className={inputClasses}
                value={addressLine2}
                onChange={(event) => setAddressLine2(event.target.value)}
                disabled={isAddressSubmitting}
              />
            </div>

            <div className={formFieldClasses}>
              <FormFieldLabel label={t("settings.profile.address.postal_code_label")} optional />
              <input
                type="text"
                className={inputClasses}
                value={addressPostalCode}
                onChange={(event) => setAddressPostalCode(event.target.value)}
                disabled={isAddressSubmitting}
              />
            </div>

            <div className={formFieldClasses}>
              <FormFieldLabel label={t("settings.profile.address.site_description_label")} optional />
              <input
                type="text"
                className={inputClasses}
                value={addressSiteDescription}
                onChange={(event) => setAddressSiteDescription(event.target.value)}
                disabled={isAddressSubmitting}
              />
            </div>

            <div className={formActionsClasses}>
              <button
                type="submit"
                className={primaryButtonClasses}
                disabled={isAddressSubmitting}
              >
                {isAddressSubmitting ? t("actions.saving") : t("settings.profile.address.save")}
              </button>
            </div>
          </form>
        </section>

        <section className={cardClasses}>
          <div className={cardHeaderClasses}>
            <div>
              <h2 className={cardTitleClasses}>{t("settings.profile.preferences.heading")}</h2>
              <p className={cardDescriptionClasses}>{t("settings.profile.preferences.description")}</p>
            </div>
          </div>

          <form className={formStackClasses} onSubmit={handlePreferencesSubmit}>
            <div className={formFieldClasses}>
              <label className={formLabelClasses} htmlFor="language-preference">
                <FormFieldLabel label={t("settings.profile.preferences.language.fieldLabel")} />
              </label>
              <LanguageSelect
                id="language-preference"
                value={preferredLocale}
                onChange={handleLocaleChange}
                options={localeOptions}
                disabled={isPreferencesSubmitting}
              />
            </div>
            <div className={formFieldClasses}>
              <FormFieldLabel label={t("settings.profile.preferences.theme.fieldLabel")} />
              <div className={themeOptionsGridClasses} role="group" aria-label={t("settings.profile.preferences.theme.fieldLabel")}>
                {(["light", "dark"] as ThemePreference[]).map((option) => {
                  const isActive = themePreference === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`${themeOptionClasses} ${isActive ? themeOptionActiveClasses : ""}`}
                      onClick={() => handleThemeSelection(option)}
                      disabled={isPreferencesSubmitting}
                    >
                      {option === "light" ? (
                        <SunDim {...defaultIconProps} aria-hidden />
                      ) : (
                        <Moon {...defaultIconProps} aria-hidden />
                      )}
                      <span>{t(`settings.profile.preferences.theme.options.${option}`)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className={formActionsClasses}>
              <button
                type="submit"
                className={primaryButtonClasses}
                disabled={isPreferencesSubmitting}
              >
                {isPreferencesSubmitting ? (
                  t("settings.profile.preferences.saving")
                ) : (
                  <>
                    <FloppyDisk {...defaultIconProps} aria-hidden />
                    {t("settings.profile.preferences.save")}
                  </>
                )}
              </button>
            </div>
          </form>
        </section>

        <section
          id="profile-email"
          tabIndex={-1}
          className={cardClasses}
        >
          <div className={cardHeaderClasses}>
            <div>
              <h2 className={cardTitleClasses}>{t("settings.profile.email.title")}</h2>
              <p className={cardDescriptionClasses}>{t("settings.profile.email.description")}</p>
            </div>
            <span
              className={`${badgeBaseClasses} ${badgeVariantClasses[emailStatusVariant as keyof typeof badgeVariantClasses]}`}
            >
              {emailStatus}
            </span>
          </div>

          {user.pending_email && (
            <p className={pendingMessageClasses} dangerouslySetInnerHTML={{ __html: t("settings.profile.email.pending_message", { email: user.pending_email }) }} />
          )}

          {showDevEmailBypass && (
            <div className="mb-4 flex flex-col gap-2 rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ClockClockwise {...defaultIconProps} aria-hidden />
                  <p className="font-semibold">
                    {t("settings.profile.email.devVerifyTitle")}
                  </p>
                </div>
                <button
                  type="button"
                  className={secondaryButtonClasses}
                  onClick={handleDevEmailBypass}
                  disabled={isDevVerifyingEmail}
                >
                  {isDevVerifyingEmail ? t("actions.verifying") : t("settings.profile.email.devVerifyButton")}
                </button>
              </div>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                {t("settings.profile.email.devVerifyHelper")}
              </p>
            </div>
          )}

          <form className={formStackClasses} onSubmit={handleEmailSubmit}>
            <div className={formFieldClasses}>
              <label className={formLabelClasses} htmlFor="profile-email-input">
                <FormFieldLabel label={t("forms.email.label")} />
              </label>
              <input
                id="profile-email-input"
                type="email"
                inputMode="email"
                className={inputClasses}
                value={emailInput}
                onChange={handleEmailInputChange}
                placeholder={t("forms.email.placeholder")}
                autoComplete="email"
                disabled={isEmailSubmitting}
                required
              />
            </div>
            <div className={formActionsClasses}>
              {canResendVerification && (
                <button
                  type="button"
                  className={ghostButtonClasses}
                  onClick={handleResendVerification}
                  disabled={isResendingVerification}
                >
                  {isResendingVerification ? (
                    t("actions.sending")
                  ) : (
                    <>
                      <PaperPlaneTilt {...defaultIconProps} aria-hidden />
                      {t("actions.resend_verification")}
                    </>
                  )}
                </button>
              )}
              <button
                type="submit"
                className={primaryButtonClasses}
                disabled={isEmailSubmitting || isEmailUnchanged}
              >
                {isEmailSubmitting ? (
                  t("actions.saving")
                ) : (
                  <>
                    <FloppyDisk {...defaultIconProps} aria-hidden />
                    {t("actions.save_email")}
                  </>
                )}
              </button>
            </div>
          </form>
        </section>

        <section
          id="profile-phone"
          tabIndex={-1}
          className={cardClasses}
        >
          <div className={cardHeaderClasses}>
            <div>
              <h2 className={cardTitleClasses}>{t("settings.profile.phone.title")}</h2>
              <p className={cardDescriptionClasses}>{t("settings.profile.phone.description")}</p>
            </div>
            <span
              className={`${badgeBaseClasses} ${badgeVariantClasses[phoneStatusVariant as keyof typeof badgeVariantClasses]}`}
            >
              {phoneStatus}
            </span>
          </div>

          {verifiedPhone && user.phone_verified && (
            <div className={`${noticeBaseClasses} ${noticeSuccessClasses}`}>
              <ShieldCheck {...defaultIconProps} aria-hidden />
              <div>
                <p className="font-semibold">{t("settings.profile.phone.verified_title")}</p>
                <p>{verifiedPhone}</p>
              </div>
            </div>
          )}

          {pendingPhone && (
            <div className={`${noticeBaseClasses} ${noticeWarningClasses}`}>
              <DeviceMobile {...defaultIconProps} aria-hidden />
              <div>
                <p className="font-semibold">{t("settings.profile.phone.pending_title")}</p>
                <p dangerouslySetInnerHTML={{ __html: t("settings.profile.phone.pending_message", { phone: pendingPhone }) }} />
              </div>
            </div>
          )}

          <div className={formStackClasses}>
            <PhoneField
              id="profile-phone-input"
              label={t("settings.profile.phone.input_label")}
              helperText={t("settings.profile.phone.helper_text")}
              value={phoneValue}
              onChange={handlePhoneValueChange}
              disabled={isRequestingPhone || isVerifyingPhone}
            />

            <div className={formActionsClasses}>
              <button
                type="button"
                className={primaryButtonClasses}
                onClick={handlePhoneRequest}
                disabled={disablePhoneRequest}
              >
                {isRequestingPhone ? t("actions.sending") : phoneRequestLabel}
              </button>
            </div>
          </div>

          {showDevPhoneBypass && (
            <div className="mt-4 flex flex-col gap-2 rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ClockClockwise {...defaultIconProps} aria-hidden />
                  <p className="font-semibold">
                    {t("settings.profile.phone.devVerifyTitle")}
                  </p>
                </div>
                <button
                  type="button"
                  className={secondaryButtonClasses}
                  onClick={handleDevPhoneBypass}
                  disabled={isDevVerifyingPhone}
                >
                  {isDevVerifyingPhone ? t("actions.verifying") : t("settings.profile.phone.devVerifyButton")}
                </button>
              </div>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                {t("settings.profile.phone.devVerifyHelper")}
              </p>
            </div>
          )}

          {didRequestPhone && (
            <div className="mt-4 flex flex-col gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className={formFieldClasses}>
                <label className={formLabelClasses} htmlFor="profile-phone-code">
                  <FormFieldLabel label={t("settings.profile.phone.code_label")} />
                </label>
                <input
                  id="profile-phone-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={inputClasses}
                  value={phoneCode}
                  onChange={(event) => setPhoneCode(event.target.value.replace(/[^0-9]/g, ""))}
                  placeholder={t("settings.profile.phone.code_placeholder")}
                  autoComplete="one-time-code"
                  disabled={isVerifyingPhone}
                />
              </div>
              <div className={formActionsClasses}>
                <button
                  type="button"
                  className={primaryButtonClasses}
                  onClick={handlePhoneVerify}
                  disabled={isVerifyingPhone || phoneCode.trim().length === 0}
                >
                  {isVerifyingPhone ? t("actions.verifying") : t("actions.verify_phone")}
                </button>
              </div>
            </div>
          )}
        </section>

        <section className={cardClasses}>
          <div className={cardHeaderClasses}>
            <div>
              <h2 className={cardTitleClasses}>{t("settings.profile.photo.title")}</h2>
              <p className={cardDescriptionClasses}>{t("settings.profile.photo.description")}</p>
            </div>
          </div>
          <div className={photoSectionClasses}>
            <Avatar
              name={user.name}
              email={user.email}
              photoUrl={profilePhotoUrl}
              fallbackUrl={profilePhotoFallbackUrl}
              size={96}
              className="ring-4 ring-white dark:ring-slate-900 shadow-sm"
            />
            <div className={photoDetailsClasses}>
              {isProfilePhotoHidden ? (
                <p>{t("settings.profile.photo.hidden_message")}</p>
              ) : (
                <p>{t("settings.profile.photo.refresh_message")}</p>
              )}
              {photoSourceLabel ? (
                <small>{t("settings.profile.photo.source_label", { source: photoSourceLabel })}</small>
              ) : (
                photoRemoteURL && <small>{t("settings.profile.photo.source_label", { source: photoRemoteURL })}</small>
              )}
              {photoCheckedAt && <small>{t("settings.profile.photo.updated_label", { date: photoCheckedAt })}</small>}
            </div>
            <div className={photoActionsClasses}>
              <button
                type="button"
                className={primaryButtonClasses}
                onClick={handlePhotoUploadClick}
                disabled={isUploadingPhoto}
              >
                {isUploadingPhoto ? t("actions.uploading") : t("actions.upload_photo")}
              </button>
              <button
                type="button"
                className={secondaryButtonClasses}
                onClick={handleProfilePhotoToggle}
                disabled={isPhotoPreferenceSubmitting || isUploadingPhoto}
              >
                {isPhotoPreferenceSubmitting
                  ? t("actions.updating")
                  : isProfilePhotoHidden
                    ? t("actions.show_photo")
                    : t("actions.hide_photo")}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_PROFILE_PHOTO_TYPES.join(",")}
              onChange={handlePhotoFileChange}
              hidden
            />
          </div>
        </section>

        <section className={cardClasses}>
          <div className={cardHeaderClasses}>
            <div>
              <h2 className={cardTitleClasses}>{t("settings.profile.password.title")}</h2>
              <p className={cardDescriptionClasses}>{t("settings.profile.password.description")}</p>
            </div>
          </div>

          <form className={formStackClasses} onSubmit={handlePasswordSubmit}>
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={user.email ?? ""}
              readOnly
              tabIndex={-1}
              aria-hidden="true"
              className="sr-only"
            />
            <div className={formFieldClasses}>
              <span className={formLabelClasses}>{t("forms.password.current_label")}</span>
              <PasswordField
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                placeholder={t("forms.password.current_placeholder")}
                autoComplete="current-password"
                disabled={isPasswordSubmitting}
                showLabel={t("forms.password.show_current_label")}
                hideLabel={t("forms.password.hide_current_label")}
                className={inputClasses}
              />
            </div>
            <div className={formFieldClasses}>
              <span className={formLabelClasses}>{t("forms.password.new_label")}</span>
              <PasswordField
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder={t("forms.password.min_length_hint")}
                autoComplete="new-password"
                disabled={isPasswordSubmitting}
                showLabel={t("forms.password.show_new_label")}
                hideLabel={t("forms.password.hide_new_label")}
                className={inputClasses}
              />
            </div>
            <div className={formFieldClasses}>
              <span className={formLabelClasses}>{t("forms.password.confirm_new_label")}</span>
              <PasswordField
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                disabled={isPasswordSubmitting}
                showLabel={t("forms.password.show_confirm_label")}
                hideLabel={t("forms.password.hide_confirm_label")}
                className={inputClasses}
              />
            </div>

            <div className={formActionsClasses}>
              <button
                type="submit"
                className={primaryButtonClasses}
                disabled={isPasswordSubmitting}
              >
                {isPasswordSubmitting ? (
                  t("actions.saving")
                ) : (
                  <>
                    <FloppyDisk {...defaultIconProps} aria-hidden />
                    {t("actions.save_password")}
                  </>
                )}
              </button>
            </div>
          </form>
        </section>
        <section className={cardClasses}>
          <div className="mb-6 flex flex-col gap-1">
            <h2 className={cardTitleClasses}>{t("auth.oauth.connected_accounts.title")}</h2>
            <p className={cardDescriptionClasses}>{t("auth.oauth.connected_accounts.description")}</p>
          </div>
          {isLoadingConnections ? (
            <SkeletonCard className="min-h-[160px]" lines={3} showAvatar />
          ) : (
            <div className="flex flex-col gap-4">
              <article className={connectionCardClasses}>
                <ConnectedAccountProvider
                  provider="google"
                  title={t("auth.oauth.google")}
                  description={
                    isGoogleConnected && googleConnection
                      ? t("auth.oauth.connected_as", { email: googleConnection.email })
                      : t("auth.oauth.not_connected")
                  }
                />
                {isGoogleConnected ? (
                  <button
                    type="button"
                    onClick={() => setConfirmAction("disconnect-google")}
                    disabled={isUnlinkingGoogle}
                    className={ghostButtonClasses}
                  >
                    {isUnlinkingGoogle ? (
                      t("auth.oauth.disconnecting")
                    ) : (
                      <>
                        <LinkSimpleBreak {...defaultIconProps} aria-hidden />
                        {t("auth.oauth.disconnect")}
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleConnectGoogle}
                    disabled={isLinkingGoogle}
                    className={secondaryButtonClasses}
                  >
                    {isLinkingGoogle ? (
                      t("auth.oauth.connecting")
                    ) : (
                      <>
                        <LinkSimple {...defaultIconProps} aria-hidden />
                        {t("auth.oauth.connect")}
                      </>
                    )}
                  </button>
                )}
              </article>

              <article className={connectionCardClasses}>
                <ConnectedAccountProvider
                  provider="facebook"
                  title={t("auth.oauth.facebook")}
                  description={
                    isFacebookConnected && facebookConnection
                      ? t("auth.oauth.connected_as", { email: facebookConnection.email })
                      : t("auth.oauth.not_connected")
                  }
                />
                {isFacebookConnected ? (
                  <button
                    type="button"
                    onClick={() => setConfirmAction("disconnect-facebook")}
                    disabled={isUnlinkingFacebook}
                    className={ghostButtonClasses}
                  >
                    {isUnlinkingFacebook ? (
                      t("auth.oauth.disconnecting")
                    ) : (
                      <>
                        <LinkSimpleBreak {...defaultIconProps} aria-hidden />
                        {t("auth.oauth.disconnect")}
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleConnectFacebook}
                    disabled={isLinkingFacebook}
                    className={secondaryButtonClasses}
                  >
                    {isLinkingFacebook ? (
                      t("auth.oauth.connecting")
                    ) : (
                      <>
                        <LinkSimple {...defaultIconProps} aria-hidden />
                        {t("auth.oauth.connect")}
                      </>
                    )}
                  </button>
                )}
              </article>
            </div>
          )}
        </section>
      </div>
      <ConfirmDialog
        open={Boolean(confirmDialogConfig)}
        title={confirmDialogConfig?.title ?? ""}
        confirmLabel={confirmDialogConfig?.confirmLabel ?? ""}
        cancelLabel={t("actions.cancel")}
        confirmTone={confirmDialogConfig?.tone ?? "default"}
        confirmLoading={Boolean(confirmDialogConfig?.loading)}
        onCancel={() => {
          if (confirmDialogConfig?.loading) return;
          setConfirmAction(null);
        }}
        onConfirm={handleConfirmAction}
      />
    </PageShell>
  );
}
